// src/contexts/portalAccess.js
//
// The only legitimate signed-in identity on the portal's default Firebase
// app is a super-admin: root users/{uid}.role === 'admin' - the SAME document
// every adminDashboardF callable checks server-side. Firebase Auth itself
// accepts any account of the project (a SimpleOffice customer can type their
// password on /login and get a session), so every portal entry point must
// resolve the role and refuse everyone else. The doc is readable by its own
// uid only (firestore.rules, root users self-get, 2026-09-07).
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase.config';

export const PORTAL_ACCESS = {
  ADMIN: 'admin',      // users/{uid}.role === 'admin'
  PENDING: 'pending',  // a portal invitee (users/{uid} exists) without admin role
  DENIED: 'denied',    // no portal identity at all (customer / employee account)
  ERROR: 'error',      // could not read - unknown, never a grant, never a sign-out
};

export const NO_ACCESS_MESSAGE =
  'This account is not an administrator of this portal. ' +
  'SimpleOffice customers sign in at simplicity-office.web.app instead.';

export const PENDING_ACCESS_MESSAGE =
  'Your portal account has no administrator access yet. ' +
  'Ask the administrator who invited you.';

export const ACCESS_ERROR_MESSAGE =
  'We could not verify your access. Check your connection and try again.';

export const messageForAccess = (access) =>
  access === PORTAL_ACCESS.PENDING ? PENDING_ACCESS_MESSAGE : NO_ACCESS_MESSAGE;

// getDoc has no client-side deadline and this Firestore client has no local
// persistence to fall back on, so on a captive portal / flaky link the read
// can sit in retry backoff forever. Every caller must get a decision.
const ACCESS_TIMEOUT_MS = 8000;

/**
 * Resolve what the signed-in account may do on the portal.
 * @param {string} uid Firebase Auth uid of the current user
 * @returns {Promise<'admin'|'pending'|'denied'|'error'>} PENDING/DENIED only
 *   when the doc was actually read; anything that prevents the read is
 *   ERROR ("unknown") - callers must not render /admin on it, but must not
 *   sign the account out either.
 */
export async function resolvePortalAccess(uid) {
  let timer = null;
  try {
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => reject(Object.assign(new Error('access check timed out'), { code: 'deadline-exceeded' })), ACCESS_TIMEOUT_MS);
    });
    const snap = await Promise.race([getDoc(doc(db, 'users', uid)), timeout]);
    if (!snap.exists()) return PORTAL_ACCESS.DENIED;
    return snap.data().role === 'admin' ? PORTAL_ACCESS.ADMIN : PORTAL_ACCESS.PENDING;
  } catch (e) {
    // permission-denied cannot mean "not an admin": the self-get rule always
    // grants a uid its own doc, and a missing doc reads fine (!exists ->
    // DENIED above). It means the rules are not deployed yet, or App Check /
    // infrastructure refused the read - unknown, never a reason to sign the
    // admin out.
    console.warn('Portal access check failed:', e && e.code);
    return PORTAL_ACCESS.ERROR;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
